import * as React from 'react';
import LinearProgress, { LinearProgressProps } from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" {...props} color="warning" />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography
                    variant="h6"
                    sx={{ color: 'text.primary' }}
                >{`${Math.round(props.value)}%`}</Typography>
            </Box>
        </Box>
    );
}


// Fisher-Yates (Knuth) shuffle algorithm
const shuffleArray = (array: string[]) => {
    let currentIndex = array.length, randomIndex;

    // While there remain elements to shuffle.
    while (currentIndex !== 0) {
        // Pick a remaining element.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
            array[randomIndex], array[currentIndex]];
    }

    return array;
};

export default function LinearWithValueLabel({
    duration = 300,
    isFinishing = false,
    onFinished,
    message,
    isActive = true,
    initialProgress = 0,
}: {
    duration: number;
    isFinishing?: boolean;
    onFinished?: () => void;
    message: string[];
    isActive?: boolean;
    initialProgress?: number;
}) {
    const [progress, setProgress] = React.useState(Math.min(initialProgress, 95));
    const [messageIndex, setMessageIndex] = React.useState(0);
    const [shuffledMessages, setShuffledMessages] = React.useState<string[]>([]);
    const progressTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
    const messageTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
    const finishTimerRef = React.useRef<ReturnType<typeof setTimeout>>();
    const hasInitializedRef = React.useRef(false);

    React.useEffect(() => {
        setShuffledMessages(shuffleArray([...message])); // Create a new array to ensure shuffle works on updates
        setMessageIndex(0); // Reset message index when message prop changes
    }, [message]);

    React.useEffect(() => {
        // Stop all timers if component is not active
        if (!isActive) {
            clearInterval(progressTimerRef.current);
            clearInterval(messageTimerRef.current);
            clearTimeout(finishTimerRef.current);
            hasInitializedRef.current = false; // Reset when inactive
            return;
        }

        if (isFinishing) {
            setProgress(100);
            clearInterval(progressTimerRef.current);
            clearInterval(messageTimerRef.current);
            if (onFinished) {
                finishTimerRef.current = setTimeout(() => {
                    onFinished();
                }, 1000); // Duration of the finishing animation
            }
        } else {
            // Use initialProgress only when first becoming active (after being inactive or on mount)
            if (!hasInitializedRef.current && initialProgress > 0) {
                setProgress(Math.min(initialProgress, 95));
                hasInitializedRef.current = true;
            }
            
            const increment = 95 / duration; // Cap at 95% until backend confirms completion
            progressTimerRef.current = setInterval(() => {
                setProgress((prevProgress) => {
                    if (prevProgress >= 95) {
                        clearInterval(progressTimerRef.current);
                        return 95; // Cap at 95% - only reach 100% when backend confirms
                    }
                    return prevProgress + increment;
                });
            }, 1000);

            // Change message every 4 seconds
            messageTimerRef.current = setInterval(() => {
                setMessageIndex((prevIndex) => {
                    const nextIndex = prevIndex + 1;
                    if (nextIndex >= shuffledMessages.length) {
                        // Reshuffle and start from beginning if all messages have been shown
                        setShuffledMessages(shuffleArray([...shuffledMessages]));
                        return 0;
                    }
                    return nextIndex;
                });
            }, 4000); // 4 seconds per message
        }

        return () => {
            clearInterval(progressTimerRef.current);
            clearInterval(messageTimerRef.current);
            clearTimeout(finishTimerRef.current); // Clear the finish timer on unmount
        };
    }, [duration, isFinishing, onFinished, shuffledMessages, isActive, initialProgress]);

    return (
        <Box sx={{ width: '100%' }}>
            <LinearProgressWithLabel
                value={progress}
                sx={{
                    '& .MuiLinearProgress-bar': {
                        transition: isFinishing ? 'transform 1s ease-in-out !important' : 'none',
                    },
                }}
            />
            <Typography
                variant="body1"
                data-testid="loader-message"
                sx={{
                    color: 'text.primary', mt: 0.4, display: 'flex', justifyContent: 'center', letterSpacing: '1px', fontWeight: 500
                }}
            >
                {shuffledMessages[messageIndex]}
            </Typography>
        </Box>
    );
}